import { useEffect, useState } from "react";
import TechLayout from "../components/TechLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Wrench, Settings, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

export default function TechTickets() {
  const [tickets, setTickets] = useState([]);
  const [napBoxes, setNapBoxes] = useState([]);
  const [availablePorts, setAvailablePorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [updateStatus, setUpdateStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  // Installation details state
  const [installationDetails, setInstallationDetails] = useState({
    nap_box_id: "",
    port_number: "",
    modem_serial_number: "",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const techType = user.technician_type || "both";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const [ticketsRes, napRes] = await Promise.all([
        axios.get(`${API}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/nap-boxes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTickets(ticketsRes.data);
      setNapBoxes(napRes.data);
    } catch (error) {
      toast.error("Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePorts = async (napBoxId) => {
    if (!napBoxId || napBoxId === "none") {
      setAvailablePorts([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API}/nap-boxes/${napBoxId}/available-ports`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setAvailablePorts(res.data);
    } catch (error) {
      console.error("Failed to fetch available ports");
      setAvailablePorts([]);
    }
  };

  const openUpdateDialog = (ticket) => {
    setSelectedTicket(ticket);
    setUpdateStatus(ticket.status);
    setRemarks(ticket.remarks || "");
    setInstallationDetails({
      nap_box_id: ticket.nap_box_id || "",
      port_number: ticket.port_number ? ticket.port_number.toString() : "",
      modem_serial_number: ticket.modem_serial_number || "",
    });
    if (ticket.nap_box_id) {
      fetchAvailablePorts(ticket.nap_box_id);
    }
    setOpen(true);
  };

  const handleNapBoxChange = (napBoxId) => {
    setInstallationDetails((prev) => ({
      ...prev,
      nap_box_id: napBoxId,
      port_number: "",
    }));
    fetchAvailablePorts(napBoxId);
  };

  const handleUpdate = async () => {
    if (!selectedTicket) return;

    try {
      const token = localStorage.getItem("token");
      const updateData = {
        status: updateStatus,
        remarks,
      };

      // Include installation details for installation tickets
      if (selectedTicket.ticket_type === "installation") {
        if (
          installationDetails.nap_box_id &&
          installationDetails.nap_box_id !== "none"
        ) {
          updateData.nap_box_id = installationDetails.nap_box_id;
        }
        if (installationDetails.port_number) {
          updateData.port_number = parseInt(installationDetails.port_number);
        }
        if (installationDetails.modem_serial_number) {
          updateData.modem_serial_number =
            installationDetails.modem_serial_number;
        }
      }

      await axios.put(`${API}/tickets/${selectedTicket.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ticket updated successfully");
      setOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update ticket");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Open: "bg-blue-100 text-blue-700 border-blue-200",
      Assigned: "bg-purple-100 text-purple-700 border-purple-200",
      Ongoing: "bg-amber-100 text-amber-700 border-amber-200",
      Done: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Closed: "bg-slate-100 text-slate-700 border-slate-200",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Open}`}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === "installation") {
      return (
        <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-700 border border-green-200 rounded-full flex items-center gap-1">
          <Settings className="w-3 h-3" /> Installation
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200 rounded-full flex items-center gap-1">
        <Wrench className="w-3 h-3" /> Repair
      </span>
    );
  };

  // Filter tickets
  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.subject?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType =
      typeFilter === "all" || ticket.ticket_type === typeFilter;

    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <TechLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </TechLayout>
    );
  }

  return (
    <TechLayout>
      <div className="p-4 sm:p-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900">
            My Assigned Tickets
          </h1>
          <p className="text-slate-600 mt-2">
            {techType === "installation" &&
              "Installation tickets assigned to you"}
            {techType === "repair" && "Repair tickets assigned to you"}
            {techType === "both" && "All tickets assigned to you"}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-48">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="repair">Repair Only</SelectItem>
              <SelectItem value="installation">Installation Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tech-tickets-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Ticket #
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider hidden sm:table-cell">
                    Client
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredTickets.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      No tickets assigned to you
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-slate-50">
                      <td className="px-4 sm:px-6 py-4 font-mono text-sm text-slate-900">
                        {ticket.ticket_number}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        {getTypeBadge(ticket.ticket_type)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-700 hidden sm:table-cell">
                        {ticket.client_name}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-sm text-slate-700 max-w-xs truncate">
                        {ticket.subject}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        {getStatusBadge(ticket.status)}
                      </td>
                      <td className="px-4 sm:px-6 py-4">
                        <Button
                          size="sm"
                          onClick={() => openUpdateDialog(ticket)}
                          data-testid={`tech-update-ticket-${ticket.id}`}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Update Ticket Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] p-0 overflow-hidden">
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle>Update Ticket</DialogTitle>
            </DialogHeader>

            <div className="px-6 pb-6">
              {selectedTicket && (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-slate-900">
                        {selectedTicket.ticket_number}
                      </p>
                      {getTypeBadge(selectedTicket.ticket_type)}
                    </div>
                    <p className="text-sm text-slate-600">
                      Client: {selectedTicket.client_name}
                    </p>
                    <p className="text-sm text-slate-700 mt-2 font-medium">
                      {selectedTicket.subject}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {selectedTicket.description}
                    </p>
                  </div>

                  <div>
                    <Label>Status</Label>
                    <Select
                      value={updateStatus}
                      onValueChange={setUpdateStatus}
                    >
                      <SelectTrigger data-testid="tech-ticket-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Assigned">Assigned</SelectItem>
                        <SelectItem value="Ongoing">Ongoing</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                        <SelectItem value="Resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Installation Details - Only for installation tickets */}
                  {selectedTicket.ticket_type === "installation" &&
                    (techType === "installation" || techType === "both") && (
                      <div className="border-t pt-4 mt-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                          Installation Details
                        </h4>
                        <p className="text-xs text-slate-500 mb-3">
                          Fill these when completing the installation
                        </p>

                        <div className="space-y-3">
                          <div>
                            <Label>NAP Box</Label>
                            <Select
                              value={installationDetails.nap_box_id || "none"}
                              onValueChange={handleNapBoxChange}
                            >
                              <SelectTrigger data-testid="tech-napbox-select">
                                <SelectValue placeholder="Select NAP Box" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  Select NAP Box
                                </SelectItem>
                                {napBoxes.map((box) => (
                                  <SelectItem key={box.id} value={box.id}>
                                    {box.name} ({box.available_ports || 0}{" "}
                                    available)
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {installationDetails.nap_box_id &&
                            installationDetails.nap_box_id !== "none" && (
                              <div>
                                <Label>Port Number</Label>
                                <Select
                                  value={
                                    installationDetails.port_number ||
                                    "select-port"
                                  }
                                  onValueChange={(val) =>
                                    val !== "select-port" &&
                                    setInstallationDetails((prev) => ({
                                      ...prev,
                                      port_number: val,
                                    }))
                                  }
                                >
                                  <SelectTrigger data-testid="tech-port-select">
                                    <SelectValue placeholder="Select port" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePorts.length === 0 ? (
                                      <SelectItem value="no-ports" disabled>
                                        No available ports
                                      </SelectItem>
                                    ) : (
                                      availablePorts.map((port) => (
                                        <SelectItem
                                          key={port.port_number}
                                          value={port.port_number.toString()}
                                        >
                                          Port #{port.port_number}
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                          <div>
                            <Label>Modem Serial Number</Label>
                            <Input
                              value={installationDetails.modem_serial_number}
                              onChange={(e) =>
                                setInstallationDetails((prev) => ({
                                  ...prev,
                                  modem_serial_number: e.target.value,
                                }))
                              }
                              placeholder="e.g., SN123456789"
                              data-testid="tech-modem-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  <div>
                    <Label>Remarks / Resolution Notes</Label>
                    <Textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Add technical notes or resolution details"
                      rows={4}
                      data-testid="tech-ticket-remarks-input"
                    />
                  </div>

                  <Button
                    onClick={handleUpdate}
                    className="w-full bg-indigo-700 hover:bg-indigo-800"
                    data-testid="tech-ticket-update-submit-btn"
                  >
                    Update Ticket
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TechLayout>
  );
}
